<script setup lang="ts">
import type { Schemas } from '#shopware'

const props = defineProps<{
    content: Schemas['CmsElementStructuredData']
}>()

type StructuredDataConfig = NonNullable<Schemas['CmsElementStructuredData']['config']>

const getValue = <K extends keyof StructuredDataConfig>(
    key: K,
): NonNullable<StructuredDataConfig[K]>['value'] | undefined => {
    return props.content?.config?.[key]?.value
}

const localBusinessSchema = computed(() => {
    const schema: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': 'SportingGoodsStore',
    }

    if (getValue('businessName')) schema.name = getValue('businessName')
    if (getValue('alternateName')) schema.alternateName = getValue('alternateName')
    if (getValue('businessImage')) schema.image = getValue('businessImage')
    if (getValue('businessUrl')) schema.url = getValue('businessUrl')
    if (getValue('businessTelephone')) schema.telephone = getValue('businessTelephone')
    if (getValue('businessPriceRange')) schema.priceRange = getValue('businessPriceRange')

    const hasAddress = getValue('streetAddress') || getValue('addressLocality') ||
        getValue('postalCode') || getValue('addressCountry')
    if (hasAddress) {
        const address: Record<string, unknown> = { '@type': 'PostalAddress' }
        if (getValue('streetAddress')) address.streetAddress = getValue('streetAddress')
        if (getValue('addressLocality')) address.addressLocality = getValue('addressLocality')
        if (getValue('postalCode')) address.postalCode = getValue('postalCode')
        if (getValue('addressRegion')) address.addressRegion = getValue('addressRegion')
        if (getValue('addressCountry')) address.addressCountry = getValue('addressCountry')
        schema.address = address
    }

    const hasGeo = getValue('latitude') || getValue('longitude')
    if (hasGeo) {
        const geo: Record<string, unknown> = { '@type': 'GeoCoordinates' }
        if (getValue('latitude')) geo.latitude = getValue('latitude')
        if (getValue('longitude')) geo.longitude = getValue('longitude')
        schema.geo = geo
    }

    const openingHours = getValue('businessOpeningHours')
    if (openingHours && Array.isArray(openingHours)) {
        schema.openingHoursSpecification = openingHours.map((hours) => {
            const spec: Record<string, unknown> = { '@type': 'OpeningHoursSpecification' }
            if (hours.opens) spec.opens = hours.opens
            if (hours.closes) spec.closes = hours.closes
            if (hours.daysOfWeek) {
                spec.dayOfWeek = hours.daysOfWeek.split(',').map((day) => day.trim())
            }
            return spec
        })
    }

    const socialMedia = getValue('socialMediaLinks')
    if (socialMedia && Array.isArray(socialMedia) && socialMedia.length > 0) {
        schema.sameAs = socialMedia
    }

    return schema
})

const organizationSchema = computed(() => {
    const schema: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
    }

    if (getValue('organizationName')) schema.name = getValue('organizationName')
    if (getValue('alternateName')) schema.alternateName = getValue('alternateName')
    if (getValue('organizationUrl')) schema.url = getValue('organizationUrl')
    if (getValue('organizationLogo')) schema.logo = getValue('organizationLogo')
    if (getValue('organizationImage')) schema.image = getValue('organizationImage')
    if (getValue('organizationDescription')) schema.description = getValue('organizationDescription')
    if (getValue('organizationTelephone')) schema.telephone = getValue('organizationTelephone')
    if (getValue('organizationEmail')) schema.email = getValue('organizationEmail')
    if (getValue('organizationVatID')) schema.vatID = getValue('organizationVatID')

    const hasAddress = getValue('streetAddress') || getValue('addressLocality') ||
        getValue('postalCode') || getValue('addressCountry')
    if (hasAddress) {
        const address: Record<string, unknown> = { '@type': 'PostalAddress' }
        if (getValue('streetAddress')) address.streetAddress = getValue('streetAddress')
        if (getValue('addressLocality')) address.addressLocality = getValue('addressLocality')
        if (getValue('postalCode')) address.postalCode = getValue('postalCode')
        if (getValue('addressRegion')) address.addressRegion = getValue('addressRegion')
        if (getValue('addressCountry')) address.addressCountry = getValue('addressCountry')
        schema.address = address
    }

    const socialMedia = getValue('socialMediaLinks')
    if (socialMedia && Array.isArray(socialMedia) && socialMedia.length > 0) {
        schema.sameAs = socialMedia
    }

    return schema
})

const structuredDataScripts = computed(() => {
    const scripts = []

    if (getValue('businessName') || getValue('businessUrl')) {
        scripts.push(jsonLdScript(localBusinessSchema.value, 'ld-local-business'))
    }

    if (getValue('organizationName') || getValue('organizationUrl')) {
        scripts.push(jsonLdScript(organizationSchema.value, 'ld-organization'))
    }

    return scripts
})

useHead(() => ({
    script: structuredDataScripts.value,
}))
</script>

<template>
    <div class="hidden" />
</template>
